<script lang="ts">
  /// 破壊的/重要操作の確認ダイアログ (shared/ui)。`window.confirm` + `window.alert` を置換し、
  /// 確認 → 非同期実行 (loading) → 失敗時はダイアログ内にエラー表示、までを内包する。
  /// FSD: 判断は持たず、実処理は onConfirm (呼び出し側) に委ねる。成功時のみ onClose を呼ぶ。
  interface Props {
    title: string;
    /// 本文。`\n` で改行できる (white-space: pre-line)。
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    /// 破壊的操作なら確認ボタンを danger 配色にする。
    danger?: boolean;
    /// 確認時の実処理。reject すると本ダイアログ内にエラーを表示し閉じない。
    onConfirm: () => Promise<void> | void;
    onClose: () => void;
  }
  let {
    title,
    message,
    confirmLabel = '実行',
    cancelLabel = 'キャンセル',
    danger = false,
    onConfirm,
    onClose,
  }: Props = $props();

  let submitting = $state(false);
  let error = $state<string | null>(null);

  async function confirm(): Promise<void> {
    submitting = true;
    error = null;
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    } finally {
      submitting = false;
    }
  }

  function onBackdropClick(ev: MouseEvent): void {
    if (ev.target === ev.currentTarget && !submitting) onClose();
  }
  function onKey(ev: KeyboardEvent): void {
    if (ev.key === 'Escape' && !submitting) onClose();
  }
</script>

<svelte:window onkeydown={onKey} />

<div class="modal-backdrop" onclick={onBackdropClick} role="presentation">
  <div class="modal-card" role="dialog" aria-modal="true" aria-label={title}>
    <h3>{title}</h3>
    <p class="message">{message}</p>
    {#if error}
      <div class="error">{error}</div>
    {/if}
    <div class="actions">
      <button type="button" class="btn" disabled={submitting} onclick={onClose}>
        {cancelLabel}
      </button>
      <button
        type="button"
        class="btn"
        class:btn-danger={danger}
        class:btn-primary={!danger}
        disabled={submitting}
        onclick={confirm}
      >
        {submitting ? '処理中…' : confirmLabel}
      </button>
    </div>
  </div>
</div>

<style>
  .modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1100;
    padding: 16px;
  }
  .modal-card {
    background: var(--panel, #ffffff);
    border-radius: var(--radius, 6px);
    width: 100%;
    max-width: 440px;
    padding: 20px;
  }
  h3 {
    margin: 0 0 8px;
    font-size: 16px;
  }
  .message {
    margin: 0 0 12px;
    color: var(--text, #1f2933);
    white-space: pre-line;
    line-height: 1.5;
  }
  .error {
    color: var(--danger, #c83232);
    background: rgba(200, 50, 50, 0.06);
    padding: 8px;
    border-radius: 4px;
    margin-bottom: 12px;
    font-size: 13px;
  }
  .actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }
  .btn-danger {
    background: var(--danger, #c83232);
    color: #fff;
    border-color: var(--danger, #c83232);
  }
</style>
